/**
 * AuthFlow - Unified authentication flow (Claude-style)
 *
 * Flow:
 * 1. User enters email or clicks Google
 * 2. Check if account exists
 *    - Exists: Show password field for login
 *    - New: Show password field for signup, then onboarding
 * 3. For Google OAuth: Auto-create account if new, then onboarding
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toaster } from './ui/toaster';
import { tokens } from '../design-system';
import apiClient from '../services/unifiedApiClient';

const AuthFlow = ({ onComplete, onStartOnboarding, lightTheme = false }) => {
  const navigate = useNavigate();
  const { login, register, getOAuthUrl, handleOAuthCallback } = useAuth();

  // Get theme-appropriate styles
  const theme = lightTheme ? lightStyles : darkStyles;

  // Flow state
  const [step, setStep] = useState('email'); // 'email' | 'password' | 'loading'
  const [isNewUser, setIsNewUser] = useState(false);

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if email exists
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await apiClient.checkEmailExists(email);
      setIsNewUser(!result.exists);
      setStep('password');
    } catch (err) {
      // If endpoint doesn't exist yet, assume new user flow
      console.warn('Email check failed, assuming new user:', err);
      setIsNewUser(true);
      setStep('password');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle login or signup with password
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (isNewUser) {
        // Register new user
        await register(email, password);
        toaster.create({
          title: 'Account created!',
          description: 'Welcome to Cahoots',
          status: 'success',
          duration: 3000,
        });
        // Start onboarding for new users
        onStartOnboarding?.({ email, isNewUser: true, provider: 'email' });
      } else {
        // Login existing user
        await login(email, password);
        toaster.create({
          title: 'Welcome back!',
          status: 'success',
          duration: 3000,
        });
        // Existing users go straight to dashboard
        onComplete?.();
        navigate('/dashboard');
      }
    } catch (err) {
      const message = err.response?.data?.detail ||
        (isNewUser ? 'Failed to create account' : 'Invalid credentials');
      setError(message);
      toaster.create({
        title: isNewUser ? 'Signup failed' : 'Login failed',
        description: message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Google OAuth
  const handleGoogleAuth = async () => {
    if (isOAuthLoading) return;

    setIsOAuthLoading(true);
    setError(null);

    try {
      // Store flag to check for onboarding after OAuth callback
      sessionStorage.setItem('check_onboarding', 'true');

      const authUrl = await getOAuthUrl('google');
      localStorage.setItem('oauth_request_time', Date.now().toString());
      localStorage.setItem('oauth_flow_started', 'true');
      window.location.href = authUrl;
    } catch (err) {
      console.error('OAuth error:', err);
      setIsOAuthLoading(false);
      setError('Failed to connect with Google');
      toaster.create({
        title: 'Google login failed',
        description: err.response?.data?.detail || 'Please try again',
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Handle dev bypass (development only)
  const handleDevBypass = () => {
    localStorage.setItem('token', 'dev-bypass-token');
    localStorage.setItem('user', JSON.stringify({
      id: 'dev-user-123',
      email: 'admin@cahoots.cc',
      username: 'admin',
      full_name: 'Admin User',
      role: 'admin',
      subscription_tier: 'enterprise',
      subscription_status: 'active',
      onboarding_completed: true,
    }));
    // Dev bypass goes straight to dashboard (admin user skips onboarding)
    window.location.href = '/dashboard';
  };

  // Go back to email step
  const handleBack = () => {
    setStep('email');
    setPassword('');
    setIsNewUser(false);
    setError(null);
  };

  return (
    <div style={theme.container}>
      {/* Google OAuth button - always visible */}
      <button
        onClick={handleGoogleAuth}
        disabled={isOAuthLoading}
        style={theme.googleButton}
      >
        <GoogleIcon />
        <span>{isOAuthLoading ? 'Connecting...' : 'Continue with Google'}</span>
      </button>

      {/* Divider */}
      <div style={theme.divider}>
        <span style={theme.dividerLine} />
        <span style={theme.dividerText}>OR</span>
        <span style={theme.dividerLine} />
      </div>

      {/* Email step */}
      {step === 'email' && (
        <form onSubmit={handleEmailSubmit}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            autoFocus
            style={theme.input}
          />
          <button
            type="submit"
            disabled={isSubmitting || !email}
            style={{
              ...theme.submitButton,
              opacity: isSubmitting || !email ? 0.6 : 1,
            }}
          >
            {isSubmitting ? 'Checking...' : 'Continue with email'}
          </button>
        </form>
      )}

      {/* Password step */}
      {step === 'password' && (
        <form onSubmit={handlePasswordSubmit}>
          {/* Show email with back button */}
          <div style={theme.emailDisplay}>
            <button type="button" onClick={handleBack} style={theme.backButton}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <span style={theme.emailText}>{email}</span>
          </div>

          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={isNewUser ? 'Create a password' : 'Enter your password'}
            required
            autoFocus
            minLength={isNewUser ? 8 : undefined}
            style={theme.input}
          />

          {isNewUser && (
            <p style={theme.passwordHint}>
              Password must be at least 8 characters
            </p>
          )}

          {error && (
            <p style={theme.error}>{error}</p>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !password}
            style={{
              ...theme.submitButton,
              opacity: isSubmitting || !password ? 0.6 : 1,
            }}
          >
            {isSubmitting
              ? (isNewUser ? 'Creating account...' : 'Signing in...')
              : (isNewUser ? 'Create account' : 'Sign in')
            }
          </button>
        </form>
      )}

      {/* Terms notice */}
      <p style={theme.terms}>
        By continuing, you acknowledge Cahoots'{' '}
        <a href="/privacy" style={theme.link}>Privacy Policy</a>
        {' '}and agree to get occasional product updates.
      </p>

      {/* Dev bypass - development only */}
      {window.CAHOOTS_CONFIG?.ENVIRONMENT === 'development' && (
        <button onClick={handleDevBypass} style={theme.devButton}>
          Dev Bypass (Skip Auth)
        </button>
      )}
    </div>
  );
};

// Google icon component
const GoogleIcon = () => (
  <svg style={{ width: '20px', height: '20px', marginRight: '12px' }} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Dark theme styles (for dark backgrounds)
const darkStyles = {
  container: {
    width: '100%',
  },
  googleButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '14px 20px',
    backgroundColor: tokens.colors.neutral[50],
    border: 'none',
    borderRadius: tokens.borderRadius.xl,
    fontSize: '15px',
    fontWeight: '600',
    color: tokens.colors.neutral[900],
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '20px 0',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: tokens.colors.neutral[700],
  },
  dividerText: {
    padding: '0 16px',
    fontSize: '12px',
    fontWeight: '500',
    color: tokens.colors.neutral[500],
    letterSpacing: '0.5px',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    backgroundColor: tokens.colors.neutral[800],
    border: `1px solid ${tokens.colors.neutral[700]}`,
    borderRadius: tokens.borderRadius.xl,
    fontSize: '15px',
    color: tokens.colors.neutral[50],
    outline: 'none',
    marginBottom: '12px',
    boxSizing: 'border-box',
  },
  submitButton: {
    width: '100%',
    padding: '14px 20px',
    backgroundColor: tokens.colors.primary[500],
    border: 'none',
    borderRadius: tokens.borderRadius.xl,
    fontSize: '15px',
    fontWeight: '600',
    color: '#ffffff',
    cursor: 'pointer',
    transition: 'opacity 0.15s ease',
  },
  emailDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
    padding: '10px 12px',
    backgroundColor: tokens.colors.neutral[800],
    borderRadius: '8px',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    backgroundColor: tokens.colors.neutral[700],
    border: 'none',
    borderRadius: '6px',
    color: tokens.colors.neutral[400],
    cursor: 'pointer',
  },
  emailText: {
    fontSize: '14px',
    color: tokens.colors.neutral[400],
  },
  passwordHint: {
    fontSize: '12px',
    color: tokens.colors.neutral[500],
    marginBottom: '12px',
    marginTop: '-8px',
  },
  error: {
    fontSize: '13px',
    color: tokens.colors.error[500],
    marginBottom: '12px',
    padding: '10px 12px',
    backgroundColor: `rgba(210, 78, 38, 0.1)`,
    borderRadius: '8px',
    border: `1px solid rgba(210, 78, 38, 0.2)`,
  },
  terms: {
    marginTop: '20px',
    fontSize: '12px',
    color: tokens.colors.neutral[500],
    textAlign: 'center',
    lineHeight: '1.5',
  },
  link: {
    color: tokens.colors.neutral[400],
    textDecoration: 'underline',
  },
  devButton: {
    width: '100%',
    marginTop: '16px',
    padding: '10px 16px',
    backgroundColor: 'transparent',
    border: `1px dashed ${tokens.colors.primary[500]}`,
    borderRadius: '8px',
    fontSize: '13px',
    color: tokens.colors.primary[400],
    cursor: 'pointer',
  },
};

// Light theme styles (for light backgrounds)
const lightStyles = {
  container: {
    width: '100%',
  },
  googleButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '14px 20px',
    backgroundColor: '#ffffff',
    border: '1.5px solid #BFAB79',
    borderRadius: tokens.borderRadius.xl,
    fontSize: '15px',
    fontWeight: '600',
    color: '#2E2109',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    boxShadow: '0 1px 0 rgba(46,33,9,0.06)',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '20px 0',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: '#D7C9A1',
  },
  dividerText: {
    padding: '0 16px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#A39068',
    letterSpacing: '0.5px',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    backgroundColor: '#ffffff',
    border: '1.5px solid #BFAB79',
    borderRadius: tokens.borderRadius.xl,
    fontSize: '15px',
    color: '#2E2109',
    outline: 'none',
    marginBottom: '12px',
    boxSizing: 'border-box',
    boxShadow: '0 1px 0 rgba(46,33,9,0.06)',
  },
  submitButton: {
    width: '100%',
    padding: '14px 20px',
    backgroundColor: tokens.colors.primary[500],
    border: 'none',
    borderRadius: tokens.borderRadius.xl,
    fontSize: '15px',
    fontWeight: '600',
    color: '#ffffff',
    cursor: 'pointer',
    transition: 'opacity 0.15s ease',
  },
  emailDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
    padding: '10px 12px',
    backgroundColor: '#EEE2BF',
    borderRadius: '8px',
    border: '1px solid #D7C9A1',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    backgroundColor: '#D7C9A1',
    border: 'none',
    borderRadius: '6px',
    color: '#6E5A38',
    cursor: 'pointer',
  },
  emailText: {
    fontSize: '14px',
    color: '#6E5A38',
  },
  passwordHint: {
    fontSize: '12px',
    color: '#6E5A38',
    marginBottom: '12px',
    marginTop: '-8px',
  },
  error: {
    fontSize: '13px',
    color: '#C14A33',
    marginBottom: '12px',
    padding: '10px 12px',
    backgroundColor: '#F2C7BA',
    borderRadius: '8px',
    border: '1px solid #C14A33',
  },
  terms: {
    marginTop: '20px',
    fontSize: '12px',
    color: '#6E5A38',
    textAlign: 'center',
    lineHeight: '1.5',
  },
  link: {
    color: '#4A3510',
    textDecoration: 'underline',
    textDecorationStyle: 'dotted',
  },
  devButton: {
    width: '100%',
    marginTop: '16px',
    padding: '10px 16px',
    backgroundColor: 'transparent',
    border: '1px dashed #BFAB79',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#EA7C1F',
    cursor: 'pointer',
  },
};

export default AuthFlow;
